import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Lets the tile's hover state reach the preview block, which is the only part of the tile with a
// border to shift. A class rather than a descendant selector on the element type, so the rule cannot
// be caught by a future nested element.
export const TEMPLATE_PREVIEW_CLASS = 'SummaryDashboardTemplatePreview';

// A real <button>, so the tile is keyboard-reachable and announces itself as actionable — the same
// treatment DashboardPromptBar's collapsed row uses. The UA button defaults have to be reset
// explicitly; only the text children carry their own font styles.
//
// At rest the tile carries no visible chrome, per the mockups; hovering raises it into a card so it
// is unmistakable which of the three the pointer is about to activate. The page behind the gallery is
// background.paper, so the border and shadow — not the fill — are what read as elevation. The border
// and transform are declared at rest as well, so hover changes their values rather than adding a box
// that would resize the tile or jump it on the first frame.
export const templateTileSx: SxProps<Theme> = {
  appearance: 'none',
  backgroundColor: 'transparent',
  border: '1px solid transparent',
  borderRadius: theme => `${theme.shape.radius.m}px`,
  // Required, not defensive: this app ships no CssBaseline and its global reset never sets
  // border-box, so `width: 100%` plus this padding and border would render the card 34px wider than
  // its grid track. Every other bordered surface in the feature declares it for the same reason.
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  padding: 2,
  textAlign: 'left',
  transform: 'translateY(0)',
  transition: theme => theme.transitions.create(
    ['background-color', 'border-color', 'box-shadow', 'transform'],
    { duration: theme.transitions.duration.short }
  ),
  width: '100%',
  // Every interactive rule is gated on :not(:disabled) rather than reset under &:disabled, because a
  // disabled button still matches :hover in Chrome — undoing four properties there would have to stay
  // in step with this block forever.
  '&:hover:not(:disabled)': {
    backgroundColor: theme => theme.palette.background.paper,
    borderColor: theme => theme.palette.neutral[200],
    boxShadow: theme => `0 8px 24px ${alpha(theme.palette.neutral[900], 0.1)}`,
    transform: 'translateY(-1px)',
  },
  // The preview keeps its own reaction on top of the card: it is the one child with a border, and
  // darkening it stops the sketch from flattening into the raised surface.
  [`&:hover:not(:disabled) .${TEMPLATE_PREVIEW_CLASS}`]: {
    borderColor: theme => theme.palette.neutral[300],
  },
  '&:focus-visible': {
    outline: theme => `2px solid ${theme.palette.brand[400]}`,
    outlineOffset: 4,
  },
  // Set while a dashboard is generating. Fading the whole tile is what explains the dead click: the
  // prompt box is locked at the same moment and its busy hint is the only other signal on screen.
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
  },
};

// Schematic sketch of the layout the prompt produces, in the same 3-column grid vocabulary the real
// dashboard uses. A CSS sketch rather than artwork design has not delivered, and rather than the
// Skeleton primitive, which animates and would read as "still loading".
//
// gridAutoRows keeps every row the same height however many the sketch needs, so the outer 4:3 box
// stays the same size across templates and the row bottoms line up.
export const templatePreviewSx: SxProps<Theme> = {
  aspectRatio: '4 / 3',
  backgroundColor: theme => theme.palette.neutral[50],
  border: theme => `1px solid ${theme.palette.neutral[200]}`,
  borderRadius: theme => `${theme.shape.radius.m}px`,
  // Without this the sketch is 26px wider than the tile's content box (its padding plus border, under
  // the content-box default this app never overrides) and spills past the hover card's right edge.
  boxSizing: 'border-box',
  display: 'grid',
  gap: 1,
  gridAutoRows: 'minmax(0, 1fr)',
  // minmax(0, 1fr) rather than 1fr so a wide cell can never widen its track.
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  padding: 1.5,
  transition: theme => theme.transitions.create(['border-color'], { duration: 160 }),
  width: '100%',
};

// A factory rather than an sx array: the components/ui Box types `sx` as a single object and rejects
// the array form MUI's own Box accepts.
export const templatePreviewCellSx = (span: number): SxProps<Theme> => ({
  backgroundColor: theme => theme.palette.neutral[200],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  gridColumn: `span ${span}`,
});

export const templateTileBadgeSx: SxProps<Theme> = {
  alignItems: 'center',
  alignSelf: 'flex-start',
  // On top of the column gap, so the preview block breathes more than the badge/title pair.
  marginTop: 1,
  backgroundColor: theme => theme.palette.brand[100],
  borderRadius: theme => `${theme.shape.radius.xs}px`,
  color: theme => theme.palette.brand[800],
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 600,
  height: 22,
  padding: '0 8px',
};

// Clamped and given a minHeight so every tile's text block is the same height and the row bottoms
// stay aligned regardless of how many lines each title and description actually wrap to.
export const templateTileTitleSx: SxProps<Theme> = {
  display: '-webkit-box',
  fontSize: '1.25rem',
  fontWeight: 700,
  lineHeight: '28px',
  minHeight: '28px',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
};

export const templateTileDescriptionSx: SxProps<Theme> = {
  color: theme => theme.palette.neutral[600],
  display: '-webkit-box',
  fontSize: 14,
  lineHeight: '20px',
  minHeight: '40px',
  overflow: 'hidden',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
};
